import { useEffect, useState } from 'react';
import { Search, Plus, Edit, Trash2, Users, User, Mail, Shield, CheckCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';

export default function UsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/users');
      const data = response.data.data || [];
      setUsers(Array.isArray(data) ? data : (data.items || []));
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const handleDelete = async (userId: string, userName: string) => {
    if (!window.confirm(`هل أنت متأكد من حذف المستخدم "${userName}"؟`)) return;
    
    setDeletingId(userId);
    try {
      const response = await api.delete(`/api/users/${userId}`);
      if (response.data.success) {
        alert('تم حذف المستخدم بنجاح');
        fetchUsers();
      } else {
        alert(response.data.message || 'فشل الحذف');
      }
    } catch (error: any) {
      console.error('Delete error:', error);
      alert(error.response?.data?.message || 'حدث خطأ أثناء الحذف');
    } finally {
      setDeletingId(null);
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = (u.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (u.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || (u.role || '').toLowerCase() === roleFilter.toLowerCase();
    return matchesSearch && matchesRole;
  });

  const getRoleColor = (role: string) => {
    const r = (role || '').toLowerCase();
    if (r.includes('admin')) return 'bg-red-100 text-red-800';
    if (r.includes('staff')) return 'bg-blue-100 text-blue-800';
    if (r.includes('delegate')) return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              المستخدمين
            </h1>
            <p className="text-gray-600 mt-2">ادارة مستخدمي النظام</p>
          </div>
          <Button onClick={() => navigate('/users/new')} className="bg-[#1e40af] hover:bg-[#1e3a8a]">
            <Plus className="w-4 h-4 ml-2" />مستخدم جديد
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center"><Users className="w-6 h-6 text-purple-600" /></div><div><p className="text-sm text-gray-600">اجمالي المستخدمين</p><p className="text-2xl font-bold">{users.length}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center"><CheckCircle className="w-6 h-6 text-green-600" /></div><div><p className="text-sm text-gray-600">نشطون</p><p className="text-2xl font-bold text-green-700">{users.filter(u => u.isActive !== false).length}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center"><Shield className="w-6 h-6 text-blue-600" /></div><div><p className="text-sm text-gray-600">الادوار</p><p className="text-2xl font-bold text-blue-700">{new Set(users.map(u => u.role)).size}</p></div></CardContent></Card>
        </div>

        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute right-3 top-3 w-4 h-4 text-gray-400" />
                <input type="text" placeholder="بحث..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pr-10 pl-4 py-2 border rounded-lg" />
              </div>
              <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="px-4 py-2 border rounded-lg">
                <option value="all">جميع الادوار</option>
                <option value="admin">مدير</option>
                <option value="staff">موظف</option>
                <option value="delegate">مندوب</option>
              </select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="bg-purple-50 border-b border-purple-200">
            <CardTitle className="flex items-center gap-2 text-purple-900">
              <Users className="w-5 h-5" />قائمة المستخدمين ({filteredUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (<div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div><p>جاري التحميل...</p></div>) :
            filteredUsers.length === 0 ? (<div className="text-center py-12"><Users className="w-16 h-16 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">لا يوجد مستخدمون</p></div>) :
            (<div className="overflow-x-auto"><table className="w-full">
              <thead className="bg-gray-50 border-b"><tr>
                <th className="text-right p-3 font-semibold text-sm">الاسم</th>
                <th className="text-right p-3 font-semibold text-sm">البريد</th>
                <th className="text-right p-3 font-semibold text-sm">الدور</th>
                <th className="text-right p-3 font-semibold text-sm">الحالة</th>
                <th className="text-right p-3 font-semibold text-sm">اجراءات</th>
              </tr></thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.userId || user.id} className="border-b hover:bg-gray-50">
                    <td className="p-3"><div className="flex items-center gap-2"><div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center"><User className="w-4 h-4 text-purple-600" /></div><span className="font-semibold">{user.name || '-'}</span></div></td>
                    <td className="p-3"><div className="flex items-center gap-1"><Mail className="w-3 h-3 text-gray-400" /><span className="text-sm">{user.email || '-'}</span></div></td>
                    <td className="p-3"><Badge className={getRoleColor(user.role)}>{user.role || '-'}</Badge></td>
                    <td className="p-3"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${user.isActive !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{user.isActive !== false ? 'نشط' : 'معطل'}</span></td>
                    <td className="p-3"><div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => navigate(`/users/edit/${user.userId}`)}><Edit className="w-4 h-4" /></Button>
                      <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(user.userId, user.name)} disabled={deletingId === user.userId}>
                        {deletingId === user.userId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </Button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table></div>)}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
